import { jsxs, jsx } from 'react/jsx-runtime';
import classNames from 'classnames';
import Autoplay from '../../node_modules/embla-carousel-autoplay/esm/embla-carousel-autoplay.esm.js';
import useEmblaCarousel from '../../node_modules/embla-carousel-react/esm/embla-carousel-react.esm.js';
import { forwardRef, useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import flattenChildren from '../../node_modules/react-keyed-flatten-children/dist/index.js';
import { ZoomIn } from '@zooplus/icons';
import { getTestLabels } from '../../utils/getTestLabels.js';
import '../Button/Button.js';
import { IconButton } from '../Button/IconButton.js';
import style from './Carousel.module.scss.js';
import { CarouselNav } from './sub-components/CarouselNav.js';
import { CarouselPagination } from './sub-components/CarouselPagination.js';
import './sub-components/CarouselSlide.js';
import { CarouselThumbNav } from './sub-components/CarouselThumbNav.js';
import { Lightbox } from './sub-components/Lightbox/Lightbox.js';
import { SlideWrap } from './sub-components/SlideWrap.js';

const { componentTestLabel, elementTestLabel } = getTestLabels('CarouselBeta');
/**
 * [design (Figma)](https://www.figma.com/design/MUnGlxUUspXSbRkFsEXWGQ/%F0%9F%92%A0-Core-Components?node-id=7811-51539&node-type=frame&t=78j1b1D2r7OjbLWy-0)
 * [source code](https://src.private.zooplus.net/projects/FE/repos/ui-components/browse/src/components/Carousel/Carousel.tsx)
 *
 * 💡 Be sure to wrap each one of your slides inside of a `CarouselSlide` component.
 *
 * @param {object} autoplay - (optional) A config object for making the carousel play automatically. If this object is provided, then the carousel will play automatically.
 *   - autoplay.delay - (optional, number) Miliseconds between each slide transition (defaults to 4000 [4 seconds]).
 *   - autoplay.pauseLabel - A label for the pause button. **Should be in the current local language**.
 *   - autoplay.playLabel - A label for the play button. **Should be in the current local language**.
 *   - autoplay.stopOnInteraction - (optional, boolean) Whether or not the autoplay feature should stop when a user manually changes a slide (defaults to `true`).
 * @param {object} emblaOptions - (optional) An {@link EmblaOptionsType} options object to pass to the underlying Embla instance.
 *   See the [Embla docs](https://www.embla-carousel.com/api/options/).
 * @param {string} gap - (optional) A CSS length string representing the gap between slides.
 * @param {string} id - A mandatory unique ID.
 * @param {boolean} insetNav - (optional) Whether or not the nav arrows should be completely layered on top of the slide(s). (default is `false`)
 * @param {object} lightbox - (optional) A config object for showing a lightbox when slides are clicked/tapped;
 *   - lightbox.ariaLabel - A label to describe the overall lightbox. **Should be in the current local language**.
 *   - lightbox.closeButtonAriaLabel - A label for the lightbox close button. **Should be in the current local language**.
 *   - lightbox.openButtonAriaLabel - A label for the lightbox open/zoom button. **Should be in the current local language**.
 *   - lightbox.title - A title for the current slide (or overall slide collection). **Should be in the current local language**.
 * @param {string} nextButtonAriaLabel - A label for the "next" button. **Should be in the current local language**.
 * @param {boolean} pagination - (optional) Whether or not the pagination "dots" should be displayed.
 * @param {string} previousButtonAriaLabel - A label for the "previous" button. **Should be in the current local language**.
 */
const Carousel = forwardRef((props, ref) => {
    const { children, className, autoplay, gap, id, insetNav, lightbox, nextButtonAriaLabel, emblaOptions, pagination, previousButtonAriaLabel, ...otherProps } = props;
    const OPTIONS = useMemo(() => ({
        ...{
            slidesToScroll: 1,
            duration: 16.5,
            inViewThreshold: 0.9,
        },
        ...emblaOptions,
    }), [emblaOptions]);
    const thumbNav = useRef([]);
    const [emblaRef, emblaApi] = useEmblaCarousel(OPTIONS, autoplay
        ? [
            Autoplay({
                delay: autoplay.delay || 4000,
                stopOnInteraction: autoplay.stopOnInteraction || true,
            }),
        ]
        : []);
    const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
        containScroll: 'keepSnaps',
        dragFree: true,
        axis: 'x',
        duration: OPTIONS.duration,
    });
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [lightboxPortalElem, setlightboxPortalElem] = useState(null);
    const [nextButtonDisabled, setnextButtonDisabled] = useState(true);
    const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
    const [scrollSnaps, setScrollSnaps] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [slidesInView, setSlidesInView] = useState([]);
    const [stylesheet, setStylesheet] = useState('');
    const isClientSide = typeof window !== 'undefined' && typeof document !== 'undefined';
    const isVertical = OPTIONS?.axis || false;
    const lightboxPortalId = `carousel-lightbox-portal-root-${id}`;
    const withLightbox = lightbox && !!lightbox?.ariaLabel;
    const onButtonAutoplayClick = useCallback((callback) => {
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (autoplay) {
            const resetOrStop = autoplay.options.stopOnInteraction === false
                ? autoplay.reset
                : autoplay.stop;
            resetOrStop();
        }
        callback();
    }, [emblaApi]);
    const scrollTo = useCallback((index) => {
        onButtonAutoplayClick(() => emblaApi && emblaApi.scrollTo(index));
    }, [onButtonAutoplayClick, emblaApi]);
    const scrollNext = useCallback(() => {
        onButtonAutoplayClick(() => emblaApi && emblaApi.scrollNext());
    }, [onButtonAutoplayClick, emblaApi]);
    const scrollPrev = useCallback(() => {
        onButtonAutoplayClick(() => emblaApi && emblaApi.scrollPrev());
    }, [onButtonAutoplayClick, emblaApi]);
    const onReinit = useCallback((emblaApi) => {
        setScrollSnaps(emblaApi.scrollSnapList());
    }, []);
    const handleSlideClick = (evt) => {
        if (!withLightbox)
            return;
        evt.stopPropagation();
        setIsLightboxOpen(true);
    };
    const handleThumbClick = useCallback((index) => {
        if (!emblaApi || !emblaThumbsApi)
            return;
        emblaApi.scrollTo(index);
    }, [emblaApi, emblaThumbsApi]);
    const handleZoomClick = (evt) => {
        if (!withLightbox)
            return;
        evt.stopPropagation();
        setIsLightboxOpen(true);
    };
    const handleToggleAutoplay = useCallback(() => {
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (!autoplay) {
            return;
        }
        const playOrStop = autoplay.isPlaying() ? autoplay.stop : autoplay.play;
        playOrStop();
    }, [emblaApi]);
    useEffect(() => {
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (!autoplay)
            return;
        setIsPlaying(autoplay.isPlaying());
        emblaApi
            .on('autoplay:play', () => setIsPlaying(true))
            .on('autoplay:stop', () => setIsPlaying(false))
            .on('reInit', () => setIsPlaying(autoplay.isPlaying()));
    }, [emblaApi]);
    const onSlidesInView = useCallback((emblaApi) => {
        if (!emblaApi)
            return;
        setSlidesInView(emblaApi.slidesInView());
    }, []);
    const onSelect = useCallback((emblaApi) => {
        if (!emblaApi)
            return;
        setSelectedIndex(emblaApi.selectedScrollSnap());
        setPrevBtnDisabled(!emblaApi.canScrollPrev());
        setnextButtonDisabled(!emblaApi.canScrollNext());
        emblaThumbsApi &&
            emblaThumbsApi.scrollTo(emblaApi.selectedScrollSnap());
    }, [emblaThumbsApi, setSelectedIndex]);
    const handleLightboxClose = () => {
        setIsLightboxOpen(false);
    };
    const handleLightboxKeyDown = (evt) => {
        if (!isLightboxOpen)
            return;
        if (evt.key === 'Escape') {
            setIsLightboxOpen(false);
        }
    };
    const handleLightboxChange = useCallback((index) => {
        scrollTo(index);
    }, [scrollTo]);
    const hasThumbNav = () => {
        if (!thumbNav.current) {
            return false;
        }
        return !thumbNav.current.some((el) => el.imgSrc === '');
    };
    useEffect(() => {
        if (!withLightbox || !isClientSide)
            return;
        if (!document.getElementById(lightboxPortalId)) {
            const el = document.createElement('div');
            el.id = lightboxPortalId;
            document.body.appendChild(el);
        }
        // @ts-expect-error TODO: fix
        setlightboxPortalElem(document.getElementById(lightboxPortalId));
    }, [isClientSide, lightboxPortalId, withLightbox]);
    useEffect(() => {
        if (!emblaApi)
            return;
        onReinit(emblaApi);
        onSelect(emblaApi);
        emblaApi.on('reInit', onReinit);
        emblaApi.on('reInit', onSelect);
        emblaApi.on('select', onSelect);
        emblaApi.on('slidesInView', onSlidesInView);
    }, [emblaApi, onReinit, onSelect, onSlidesInView]);
    useEffect(() => {
        const thumbArr = [];
        flattenChildren(children).forEach((slide) => {
            const { isVideo, thumb } = slide.props;
            thumbArr.push({
                imgSrc: thumb || '',
                isVideo: isVideo,
            });
        });
        thumbNav.current = thumbArr;
    }, [children]);
    useEffect(() => {
        let styles = `
        :where([data-z-carousel-beta-id="${id}"]) {
          --pp-CarouselBeta-slides-to-scroll: ${OPTIONS.slidesToScroll || 1};
          ${`--pp-CarouselBeta-slide-spacing: ${gap || '0.75rem'};`}
          ${isVertical
            ? `
                --pp-CarouselBeta-margin-inline: 0;
                --pp-CarouselBeta-slide-track-height: 720px;
              `
            : ''}
          --pp-CarouselBeta-loader-img:
            url(data:image/gif;base64,R0lGODlhjACMAJEDAO3t7dvb2////////yH/C05FVFNDQVBFMi4wAwEAAAAh+QQFHgADACwAAAAAjACMAEACrJSPqcvtD6OctNqLs968+w+G4kiW5omm6sq27gvH8kzX9o3n+s73/g8MCofEovGITCqXzKbzCY1Kp9Sq9YrNarfcrvcLDovH5LL5jE6r1+y2+w2Py+f0uv2Oz+v3/L7/DxgoOEhYaHiImKi4yNjo+AgZKTlJWWl5iZmpucnZ6fkJGio6SlpqeoqaqrrK2ur6ChsrO0tba3uLm6u7y9vr+wscLDxMXGx8jJxcVgAAIfkEBR4AAwAsHwBXABgAGgBAAmKcjwMRkLkYM9ENiV3EXKpuPeADYR9nVc/GHUuqdXJD1hRqs43ujDdd851mjBDxWLxkSDpJyCZaQlVKaTDTNOCGyVbT6Ly2mIBXiPXkCn80pKfqlpVB8WS5XEKmpzKoGFhTAAAh+QQFHgADACxGAEcAGgAZAEACZpxvgAPhuiKYizKHX8s8q+1FxwaJ5sUNXWCC65Ok5ssyjSW6X1fmqetAwRDAAE7FQ9JCo6OQNlm1pKfpENgTdZRMKybKKWUPGfL3GaxqqeiwWVZ8NbNLN/pdV6zGS71snaQWEVhVAAAh+QQFHgADACxTABsAGAAaAEACYpyPAxGQuRgz0Q2JXcRcqm494ANhH2dVz8YdS6p1ckPWFGqzje6MN13znWaMEPFYvGRIOknIJlpCVUppMNM04IbJVtPovLaYgFeI9eQKfzSkp+qWlUHxZLlcQqanMqgYWFMAACH5BAUeAAMALB8AVwAYABoAQAIXlI+py+0Po5y02ouz3rz7D4biSJZmWAAAIfkEBR4AAwAsRgBHABoAGQBAAheUj6nL7Q+jnLTai7PevPsPhuJIluaJFQA7);
        }
      `;
        if (OPTIONS?.breakpoints) {
            Object.keys(OPTIONS.breakpoints).forEach((bp) => {
                if (!OPTIONS?.breakpoints) {
                    return;
                }
                const slidesToScroll = OPTIONS?.breakpoints[bp]?.slidesToScroll;
                if (!slidesToScroll) {
                    return;
                }
                styles += `@media ${bp} { [data-z-carousel-beta-id="${id}"] { --pp-CarouselBeta-slides-to-scroll: ${slidesToScroll}; } } `;
            });
        }
        setStylesheet(styles);
    }, [gap, insetNav, isVertical, OPTIONS, id]);
    return (jsxs("div", { "data-zta": componentTestLabel, ...otherProps, ref: ref, "data-z-carousel-beta-id": id, id: id, className: classNames(style['z-carousel-beta'], {
            [style['z-carousel-beta--has-thumbs']]: hasThumbNav(),
            [style['z-carousel-beta--has-inset-nav']]: insetNav,
            [style['z-carousel-beta--has-pagination']]: pagination,
            [style['z-carousel-beta--vertical']]: isVertical,
        }, className), children: [!!stylesheet && jsx("style", { children: stylesheet }), jsxs("div", { "data-zta": elementTestLabel('Main'), className: style['z-carousel-beta__main'], children: [jsx("div", { className: style['z-carousel-beta__main-inner'], children: jsx("div", { "data-zta": elementTestLabel('SlideTrack'), ref: emblaRef, className: style['z-carousel-beta__slide-track'], children: jsx("div", { className: style['z-carousel-beta__slide-track-inner'], children: flattenChildren(children).map((child, index) => {
                                    return (jsx(SlideWrap, { "data-position": index + 1, "data-active": slidesInView.includes(index), onSlideClick: handleSlideClick, isSelected: slidesInView.includes(index), children: child }, index));
                                }) }) }) }), jsx(CarouselNav, { nextButtonAriaLabel: nextButtonAriaLabel, nextClick: scrollNext, nextButtonDisabled: nextButtonDisabled, previousButtonAriaLabel: previousButtonAriaLabel, prevClick: scrollPrev, previousButtonDisabled: prevBtnDisabled }), withLightbox && (jsx(IconButton, { ariaLabel: lightbox.openButtonAriaLabel, onClick: handleZoomClick, className: style['z-carousel-beta__zoom'], size: "large", variant: "ghost", children: jsx(ZoomIn, { "aria-hidden": true }) }))] }), hasThumbNav() && (jsx(CarouselThumbNav, { items: thumbNav.current, nextButtonAriaLabel: nextButtonAriaLabel, nextButtonDisabled: nextButtonDisabled, onScrollNext: scrollNext, onScrollPrev: scrollPrev, onThumbClick: handleThumbClick, previousButtonAriaLabel: nextButtonAriaLabel, previousButtonDisabled: prevBtnDisabled, ref: emblaThumbsRef, selectedIndex: selectedIndex })), pagination && (jsx(CarouselPagination, { arialabel: "test", autoplay: !!autoplay, isPlaying: isPlaying, items: scrollSnaps, onToggleAutoPlay: handleToggleAutoplay, pauseLabel: autoplay?.pauseLabel, playLabel: autoplay?.playLabel, scrollTo: scrollTo, selectedIndex: selectedIndex })), withLightbox &&
                isClientSide &&
                isLightboxOpen &&
                createPortal(jsx(Lightbox, { nextButtonAriaLabel: nextButtonAriaLabel, previousButtonAriaLabel: previousButtonAriaLabel, closeButtonAriaLabel: lightbox.closeButtonAriaLabel, handleKeyDown: handleLightboxKeyDown, initialSlide: emblaApi?.selectedScrollSnap() || 0, isOpen: isLightboxOpen, onClose: handleLightboxClose, onSlideChange: handleLightboxChange, title: lightbox.title, ariaLabel: lightbox.ariaLabel, children: children }), lightboxPortalElem || document.body)] }));
});
Carousel.displayName = 'Carousel';
const CarouselBeta = Carousel;
CarouselBeta.displayName = 'CarouselBeta';

export { Carousel, CarouselBeta };
//# sourceMappingURL=Carousel.js.map
